// redux/slices/clientSettingsSlice.js

import {
  createAsyncThunk,
  createSlice,
} from "@reduxjs/toolkit";

import { resetStore } from "./resetStore";
import { clearJwtToken } from "../../Utils/authStorage";
import { apiRequest } from "../../Utils/apiClient";

/* ========================= HELPERS ========================= */

const getError = (error, fallback) =>
  error?.response?.data?.error ||
  error?.response?.data?.message ||
  error?.response?.data ||
  error?.message ||
  fallback;

const clearTokenOnUnauthorized = async (error) => {
  const status = error?.response?.status;

  if (status === 401 || status === 403) {
    try {
      await clearJwtToken();
    } catch {}
  }
};

const normalizeSettings = (payload = {}) => ({
  autoAddClients:
    payload?.autoAddClients ?? false,

  reviewRequestMode:
    payload?.reviewRequestMode ?? "all",

  unifiedClientLanguage:
    payload?.unifiedClientLanguage ?? "off",
});

/* ========================= THUNKS ========================= */

// GET /client-settings/:uid
export const fetchClientSettings = createAsyncThunk(
  "clientSettings/fetchClientSettings",
  async (uid, { rejectWithValue }) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      const response = await apiRequest(
        "GET",
        `/client-settings/${encodeURIComponent(uid)}`
      );

      const data = response?.data ?? response;
      return normalizeSettings(data);
    } catch (error) {
      const status = error?.response?.status;

      if (status === 404) {
        return normalizeSettings();
      }

      await clearTokenOnUnauthorized(error);

      return rejectWithValue(
        getError(error, "fetchClientSettings failed")
      );
    }
  }
);

// PATCH /client-settings/:uid
export const updateClientSettings = createAsyncThunk(
  "clientSettings/updateClientSettings",
  async ({ uid, ...patch }, { rejectWithValue }) => {
    try {
      if (!uid) {
        throw new Error("UID не передан");
      }

      const allowedPatch = {};

      if (Object.prototype.hasOwnProperty.call(patch, "autoAddClients")) {
        allowedPatch.autoAddClients = Boolean(patch.autoAddClients);
      }

      if (Object.prototype.hasOwnProperty.call(patch, "reviewRequestMode")) {
        allowedPatch.reviewRequestMode = patch.reviewRequestMode;
      }

      if (Object.prototype.hasOwnProperty.call(patch, "unifiedClientLanguage")) {
        allowedPatch.unifiedClientLanguage = patch.unifiedClientLanguage;
      }

      if (!Object.keys(allowedPatch).length) {
        throw new Error("Настройки для обновления не переданы");
      }

      const response = await apiRequest(
        "PATCH",
        `/client-settings/${encodeURIComponent(uid)}`,
        allowedPatch
      );

      const data = response?.data ?? response;

      return {
        uid,
        patch: allowedPatch,
        settings: {
          ...allowedPatch,
          ...(data || {}),
        },
      };
    } catch (error) {
      await clearTokenOnUnauthorized(error);

      return rejectWithValue(
        getError(error, "updateClientSettings failed")
      );
    }
  }
);

/* ========================= SELECTORS ========================= */

export const selectClientSettings = (state) => ({
  autoAddClients: state.clientSettings?.autoAddClients ?? false,
  reviewRequestMode: state.clientSettings?.reviewRequestMode ?? "all",
  unifiedClientLanguage: state.clientSettings?.unifiedClientLanguage ?? "off",
});

export const selectAutoAddClients = (state) =>
  state.clientSettings?.autoAddClients ?? false;

export const selectReviewRequestMode = (state) =>
  state.clientSettings?.reviewRequestMode ?? "all";

export const selectUnifiedClientLanguage = (state) =>
  state.clientSettings?.unifiedClientLanguage ?? "off";

export const selectClientSettingsStatus = (state) =>
  state.clientSettings?.status ?? "idle";

export const selectClientSettingsError = (state) =>
  state.clientSettings?.error ?? null;

/* ========================= STATE ========================= */

const initialState = {
  autoAddClients: false,
  reviewRequestMode: "all",
  unifiedClientLanguage: "off",

  status: "idle",
  updateStatus: "idle",
  error: null,
};

/* ========================= SLICE ========================= */

const clientSettingsSlice = createSlice({
  name: "clientSettings",
  initialState,

  reducers: {
    patchClientSettings: (state, action) => {
      const patch = action.payload || {};

      if (Object.prototype.hasOwnProperty.call(patch, "autoAddClients")) {
        state.autoAddClients = Boolean(patch.autoAddClients);
      }

      if (Object.prototype.hasOwnProperty.call(patch, "reviewRequestMode")) {
        state.reviewRequestMode = patch.reviewRequestMode;
      }

      if (Object.prototype.hasOwnProperty.call(patch, "unifiedClientLanguage")) {
        state.unifiedClientLanguage = patch.unifiedClientLanguage;
      }
    },

    clearClientSettingsError: (state) => {
      state.error = null;
    },

    resetClientSettingsStatus: (state) => {
      state.status = "idle";
      state.updateStatus = "idle";
      state.error = null;
    },

    clearClientSettings: () => initialState,
  },

  extraReducers: (builder) => {
    builder
      .addCase(fetchClientSettings.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchClientSettings.fulfilled, (state, action) => {
        state.status = "succeeded";

        const settings = normalizeSettings(action.payload);
        state.autoAddClients = settings.autoAddClients;
        state.reviewRequestMode = settings.reviewRequestMode;
        state.unifiedClientLanguage = settings.unifiedClientLanguage;
      })
      .addCase(fetchClientSettings.rejected, (state, action) => {
        state.status = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "fetchClientSettings failed";
      })

      .addCase(updateClientSettings.pending, (state) => {
        state.updateStatus = "loading";
        state.error = null;
      })
      .addCase(updateClientSettings.fulfilled, (state, action) => {
        state.updateStatus = "succeeded";

        const settings =
          action.payload?.settings ||
          action.payload?.patch ||
          {};

        if (Object.prototype.hasOwnProperty.call(settings, "autoAddClients")) {
          state.autoAddClients = Boolean(settings.autoAddClients);
        }

        if (Object.prototype.hasOwnProperty.call(settings, "reviewRequestMode")) {
          state.reviewRequestMode = settings.reviewRequestMode;
        }

        if (Object.prototype.hasOwnProperty.call(settings, "unifiedClientLanguage")) {
          state.unifiedClientLanguage = settings.unifiedClientLanguage;
        }
      })
      .addCase(updateClientSettings.rejected, (state, action) => {
        state.updateStatus = "failed";
        state.error =
          action.payload ||
          action.error?.message ||
          "updateClientSettings failed";
      })

      .addCase(resetStore, () => initialState);
  },
});

/* ========================= ACTIONS ========================= */

export const {
  patchClientSettings,
  clearClientSettingsError,
  resetClientSettingsStatus,
  clearClientSettings,
} = clientSettingsSlice.actions;

export default clientSettingsSlice.reducer;
